// i18n Translations Dictionary
// DACAMS acronyms are kept in English across both languages

export type Language = 'es' | 'en';

export const translations = {
    // ============= COMMON =============
    common: {
        loading: { es: 'Cargando...', en: 'Loading...' },
        save: { es: 'Guardar', en: 'Save' },
        cancel: { es: 'Cancelar', en: 'Cancel' },
        delete: { es: 'Eliminar', en: 'Delete' },
        edit: { es: 'Editar', en: 'Edit' },
        close: { es: 'Cerrar', en: 'Close' },
        confirm: { es: 'Confirmar', en: 'Confirm' },
        back: { es: 'Volver', en: 'Back' },
        next: { es: 'Siguiente', en: 'Next' },
        previous: { es: 'Anterior', en: 'Previous' },
        search: { es: 'Buscar', en: 'Search' },
        filter: { es: 'Filtrar', en: 'Filter' },
        export: { es: 'Exportar', en: 'Export' },
        import: { es: 'Importar', en: 'Import' },
        download: { es: 'Descargar', en: 'Download' },
        upload: { es: 'Subir', en: 'Upload' },
        yes: { es: 'Sí', en: 'Yes' },
        no: { es: 'No', en: 'No' },
        all: { es: 'Todos', en: 'All' },
        none: { es: 'Ninguno', en: 'None' },
    },

    // ============= NAVIGATION =============
    nav: {
        home: { es: 'Inicio', en: 'Home' },
        projects: { es: 'Proyectos', en: 'Projects' },
        settings: { es: 'Configuración', en: 'Settings' },
        profile: { es: 'Perfil', en: 'Profile' },
        logout: { es: 'Cerrar Sesión', en: 'Logout' },
        newProject: { es: 'Nuevo Proyecto', en: 'New Project' },
    },

    // ============= HOME PAGE =============
    home: {
        title: { es: 'Mis Proyectos', en: 'My Projects' },
        subtitle: { es: 'Gestiona tus análisis de capacidad aeroportuaria', en: 'Manage your airport capacity analyses' },
        noProjects: { es: 'No hay proyectos', en: 'No projects' },
        createFirst: { es: 'Crea tu primer proyecto', en: 'Create your first project' },
        recentProjects: { es: 'Proyectos Recientes', en: 'Recent Projects' },
        allProjects: { es: 'Todos los Proyectos', en: 'All Projects' },
    },

    // ============= PROJECT =============
    project: {
        name: { es: 'Nombre del Proyecto', en: 'Project Name' },
        airport: { es: 'Aeropuerto', en: 'Airport' },
        status: { es: 'Estado', en: 'Status' },
        created: { es: 'Creado', en: 'Created' },
        updated: { es: 'Actualizado', en: 'Updated' },
        analyzing: { es: 'Analizando...', en: 'Analyzing...' },
        completed: { es: 'Completado', en: 'Completed' },
        pending: { es: 'Pendiente', en: 'Pending' },
        failed: { es: 'Error', en: 'Failed' },
        rerunAnalysis: { es: 'Re-ejecutar Análisis', en: 'Re-run Analysis' },
        deleteProject: { es: 'Eliminar Proyecto', en: 'Delete Project' },
        downloadReport: { es: 'Descargar Informe', en: 'Download Report' },
    },

    // ============= TABS =============
    tabs: {
        overview: { es: 'Resumen', en: 'Overview' },
        parameters: { es: 'Parámetros', en: 'Parameters' },
        heatmaps: { es: 'Heatmaps', en: 'Heatmaps' },
        kpis: { es: 'KPIs', en: 'KPIs' },
        reports: { es: 'Informes', en: 'Reports' },
    },

    // ============= OVERVIEW TAB =============
    overview: {
        apcasResults: { es: 'Resultados AP-CAM', en: 'AP-CAM Results' },
        strategicIndicators: { es: 'Indicadores Estratégicos', en: 'Strategic Indicators' },
        demandCurve: { es: 'Curva Demanda vs Capacidad', en: 'Demand vs Capacity Curve' },
        hourlyMovements: { es: 'Movimientos por Hora', en: 'Hourly Movements' },
        capacity: { es: 'Capacidad', en: 'Capacity' },
        demand: { es: 'Demanda', en: 'Demand' },
        busyDay: { es: 'IATA Busy Day', en: 'IATA Busy Day' },
    },

    // ============= HEATMAPS TAB =============
    heatmaps: {
        interactiveMap: { es: 'Mapa Interactivo', en: 'Interactive Map' },
        heatmapTracks: { es: 'Heatmaps (Tracks)', en: 'Heatmaps (Tracks)' },
        pushbackAnalysis: { es: 'Pushback Analysis', en: 'Pushback Analysis' },
        taxiRisk: { es: 'Taxi Risk & Hotspots', en: 'Taxi Risk & Hotspots' },
        taxiSpeed: { es: 'Taxi Speed Map', en: 'Taxi Speed Map' },
        congestion: { es: 'Congestión (Slow Speed)', en: 'Congestion (Slow Speed)' },
        arrivals: { es: 'Rutas de Llegada', en: 'Arrival Routes' },
        departures: { es: 'Rutas de Salida', en: 'Departure Routes' },
        allTracks: { es: 'Todos los Tracks', en: 'All Tracks' },
    },

    // ============= MAP DESCRIPTIONS =============
    mapDescriptions: {
        heatmap: {
            es: 'Visualiza la densidad de movimientos en la plataforma. Las zonas más brillantes indican mayor concentración de tráfico.',
            en: 'Visualizes movement density on the apron. Brighter zones indicate higher traffic concentration.'
        },
        pushback: {
            es: 'Muestra las zonas de seguridad durante operaciones de pushback. Los conos rojos representan el área de jet blast.',
            en: 'Shows safety zones during pushback operations. Red cones represent the jet blast area.'
        },
        taxiRisk: {
            es: 'Identifica zonas de alto tráfico y potencial conflicto. Cada punto representa una posición registrada de aeronave.',
            en: 'Identifies high traffic zones and potential conflicts. Each point represents a recorded aircraft position.'
        },
        taxiSpeed: {
            es: 'Muestra la velocidad mediana en cada zona de la plataforma. Rojo=lento, Amarillo=moderado, Verde=normal, Azul=rápido.',
            en: 'Shows median speed in each apron zone. Red=slow, Yellow=moderate, Green=normal, Blue=fast.'
        },
    },

    // ============= KPIs =============
    kpis: {
        operationalEfficiency: { es: 'Operational Efficiency KPIs', en: 'Operational Efficiency KPIs' },
        apronTaxiIn: { es: 'Apron Taxi-In', en: 'Apron Taxi-In' },
        apronTaxiOut: { es: 'Apron Taxi-Out', en: 'Apron Taxi-Out' },
        avgTaxiSpeed: { es: 'Avg Taxi Speed', en: 'Avg Taxi Speed' },
        tobtAdherence: { es: 'TOBT Adherence', en: 'TOBT Adherence' },
        pushbackDelay: { es: 'Pushback Delay', en: 'Pushback Delay' },
        flowAdherence: { es: 'Flow Adherence', en: 'Flow Adherence' },
        standOccupancy: { es: 'Stand Occupancy %', en: 'Stand Occupancy %' },
        standReassignment: { es: 'Stand Reassignment', en: 'Stand Reassignment' },
        taxiRouteAdherence: { es: 'Taxi Route Adherence', en: 'Taxi Route Adherence' },
    },

    // ============= KPI DESCRIPTIONS =============
    kpiDescriptions: {
        apronTaxiIn: {
            es: 'Tiempo mediano de rodaje de llegada desde el punto de handover ATC hasta el stand.',
            en: 'Median taxi time from ATC handover point to stand (In-Block).'
        },
        apronTaxiOut: {
            es: 'Tiempo mediano de rodaje de salida desde el stand hasta el punto de handover ATC.',
            en: 'Median taxi time from stand (Off-Block) to ATC handover point.'
        },
        avgTaxiSpeed: {
            es: 'Velocidad mediana de rodaje en la plataforma. Velocidades bajas indican congestión.',
            en: 'Median taxi speed on apron. Low speeds indicate congestion.'
        },
        tobtAdherence: {
            es: 'Diferencia entre AOBT y TOBT (o EOBT). Mide eficiencia del AMS.',
            en: 'Difference between AOBT and TOBT (or EOBT). Measures AMS efficiency.'
        },
        pushbackDelay: {
            es: 'Tiempo de reacción del equipo de tierra desde ASAT hasta el inicio del pushback.',
            en: 'Ground handler reaction time from ASAT to actual pushback start.'
        },
        flowAdherence: {
            es: 'Cálculo A-CDM fundamental. TSAT = ventana asignada por secuencia de pista. Crítico para EUROCONTROL.',
            en: 'Fundamental A-CDM calculation. TSAT = assigned runway sequence window. Critical for EUROCONTROL.'
        },
        standOccupancy: {
            es: 'Porcentaje de aeronaves que desocupan el stand dentro de la ventana SIBT-SOBT.',
            en: 'Percentage of aircraft vacating stand within SIBT-SOBT window.'
        },
        standReassignment: {
            es: 'Ratio de cambios de stand no planificados vs Stand Allocation Plan original.',
            en: 'Ratio of unplanned stand changes vs original Stand Allocation Plan.'
        },
        taxiRouteAdherence: {
            es: 'Desviaciones de rutas de rodaje o procedimientos de pushback asignados.',
            en: 'Deviations from assigned taxi routes or pushback procedures.'
        },
    },

    // ============= PARAMETERS =============
    parameters: {
        smeInputs: { es: 'Parámetros SME', en: 'SME Parameters' },
        dacamsInputs: { es: 'Parámetros DACAMS', en: 'DACAMS Parameters' },
        saveChanges: { es: 'Guardar Cambios', en: 'Save Changes' },
        resetDefaults: { es: 'Restaurar Valores', en: 'Reset Defaults' },
    },

    // ============= APCAS BLOCKS =============
    apcas: {
        block1: { es: 'Block 1 - Baseline & Intensity', en: 'Block 1 - Baseline & Intensity' },
        block2: { es: 'Block 2 - Complexity', en: 'Block 2 - Complexity' },
        block3: { es: 'Block 3 - Efficiency', en: 'Block 3 - Efficiency' },
        block4: { es: 'Block 4 - Capacity', en: 'Block 4 - Capacity' },
        block5: { es: 'Block 5 - Saturation', en: 'Block 5 - Saturation' },
        block6: { es: 'Block 6 - Management', en: 'Block 6 - Management' },
        block7: { es: 'Block 7 - Equilibrium', en: 'Block 7 - Equilibrium' },
    },

    // ============= SETTINGS =============
    settings: {
        title: { es: 'Configuración', en: 'Settings' },
        appearance: { es: 'Apariencia', en: 'Appearance' },
        darkMode: { es: 'Modo Oscuro', en: 'Dark Mode' },
        lightMode: { es: 'Modo Claro', en: 'Light Mode' },
        language: { es: 'Idioma', en: 'Language' },
        spanish: { es: 'Español', en: 'Spanish' },
        english: { es: 'Inglés', en: 'English' },
    },

    // ============= AUTH =============
    auth: {
        login: { es: 'Iniciar Sesión', en: 'Login' },
        logout: { es: 'Cerrar Sesión', en: 'Logout' },
        email: { es: 'Correo Electrónico', en: 'Email' },
        password: { es: 'Contraseña', en: 'Password' },
        forgotPassword: { es: '¿Olvidaste tu contraseña?', en: 'Forgot password?' },
        signUp: { es: 'Registrarse', en: 'Sign Up' },
    },

    // ============= ERRORS & MESSAGES =============
    messages: {
        success: { es: 'Operación exitosa', en: 'Operation successful' },
        error: { es: 'Ha ocurrido un error', en: 'An error occurred' },
        confirmDelete: { es: '¿Estás seguro de eliminar?', en: 'Are you sure you want to delete?' },
        noData: { es: 'No hay datos disponibles', en: 'No data available' },
        analysisInProgress: { es: 'Análisis en progreso...', en: 'Analysis in progress...' },
        analysisComplete: { es: 'Análisis completado', en: 'Analysis complete' },
    },
} as const;

// Type-safe translation key type
export type TranslationKey = keyof typeof translations;
export type TranslationSubKey<K extends TranslationKey> = keyof typeof translations[K];
